import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "../firebase";
import { Heading } from "./heading";
import { Dialog, DialogBody, DialogActions } from "./dialog";
import { Button } from "./button";
import {
  FunnelIcon,
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EnvelopeIcon,
  PlusCircleIcon,
  XMarkIcon,
  ListBulletIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/20/solid";
import { showAlert } from "../alertService";


const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  try {
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat("es-MX", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "America/Mexico_City",
    }).format(date);
  } catch (error) {
    return "Invalid date";
  }
};

const ROLES = [
  { id: "admin", name: "Administrador" },
  { id: "client", name: "Cliente" },
  { id: "planner", name: "Planner" },
  { id: "owner", name: "Owner" },
  { id: "viewer", name: "Visor" },
  // Solo lectura a todo el venue salvo bitácora (P-FRA-13).
  { id: "inversionista", name: "Inversionista" },
];

// Roles que viven ligados a venues (users/{uid}.venues).
const ROLES_CON_VENUES = ["owner", "viewer", "inversionista"];

const FILTER_ROLES = [{ id: "all", name: "Todos los roles" }, ...ROLES];

const SORT_FIELDS = {
  LAST_LOGIN: "lastLogin",
  CREATED_AT: "createdAt",
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [emailSearchQuery, setEmailSearchQuery] = useState("");
  const [selectedVenueFilter, setSelectedVenueFilter] = useState("all");
  const [sortField, setSortField] = useState(SORT_FIELDS.LAST_LOGIN);
  const [sortDirection, setSortDirection] = useState("desc");
  const [updatingRoleId, setUpdatingRoleId] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState("");
  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false);
  const [pendingRoleChange, setPendingRoleChange] = useState(null);
  const [removingVenue, setRemovingVenue] = useState(null);
  const [viewMode, setViewMode] = useState("list");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch users
        const usersCollection = collection(db, "users");
        const usersSnapshot = await getDocs(usersCollection);
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersData);

        // Fetch venues
        const venuesCollection = collection(db, "venues");
        const venuesSnapshot = await getDocs(venuesCollection);
        const venuesData = venuesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setVenues(venuesData);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Error fetching data");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (updatingRoleId && !event.target.closest(".role-dropdown")) {
        setUpdatingRoleId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [updatingRoleId]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ChevronUpDownIcon className="h-4 w-4" />;
    return sortDirection === "asc" ? (
      <ChevronUpIcon className="h-4 w-4" />
    ) : (
      <ChevronDownIcon className="h-4 w-4" />
    );
  };

  const filteredUsers = users
    .filter((user) => {
      const matchesRole = selectedRole === "all" || user.role === selectedRole;
      const searchTerm = searchQuery.toLowerCase();
      const emailTerm = emailSearchQuery.toLowerCase();

      const matchesSearch =
        !searchQuery ||
        user.name?.toLowerCase().includes(searchTerm) ||
        user.lastName?.toLowerCase().includes(searchTerm);

      const matchesEmail =
        !emailSearchQuery || user.email?.toLowerCase().includes(emailTerm);

      const matchesVenue =
        selectedVenueFilter === "all" ||
        (user.venues && user.venues.includes(selectedVenueFilter));

      return matchesRole && matchesSearch && matchesEmail && matchesVenue;
    })
    .sort((a, b) => {
      // Usuarios sin valor (p. ej. nunca han hecho login) siempre al final —
      // devolver 0 aquí rompe la transitividad y desordena TODA la lista.
      const aRaw = a[sortField];
      const bRaw = b[sortField];
      if (!aRaw && !bRaw) return 0;
      if (!aRaw) return 1;
      if (!bRaw) return -1;

      const aValue = aRaw.toDate?.() || aRaw;
      const bValue = bRaw.toDate?.() || bRaw;
      const cmp = aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return sortDirection === "asc" ? cmp : -cmp;
    });

  const venueGroups = venues.map((venue) => {
    const venueUsers = users.filter(
      (user) => user.venues && user.venues.includes(venue.id)
    );
    return { venue, users: venueUsers };
  });

  const usersWithoutVenue = users.filter(
    (user) => !user.venues || user.venues.length === 0
  );

  const handleRoleChange = async (userId, newRole) => {
    const user = users.find((u) => u.id === userId);
    if (
      user &&
      ROLES_CON_VENUES.includes(user.role) &&
      user.venues &&
      user.venues.length > 0 &&
      !ROLES_CON_VENUES.includes(newRole)
    ) {
      showAlert(
        "No puedes cambiar el rol de este usuario mientras tenga venues asignados. Elimina todos los venues antes de cambiar el rol.",
        "error"
      );
      setUpdatingRoleId(null);
      return;
    }

    try {
      setUpdatingRoleId(userId);
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        role: newRole,
        updatedAt: new Date(),
      });

      // Update local state
      setUsers(
        users.map((user) =>
          user.id === userId ? { ...user, role: newRole } : user
        )
      );

      showAlert("Rol actualizado exitosamente", "success");
      setUpdatingRoleId(null);
    } catch (error) {
      console.error("Error updating role:", error);
      showAlert("Error al actualizar el rol", "error");
      setUpdatingRoleId(null);
    }
  };

  const handleVenueAssignment = async () => {
    if (!pendingRoleChange || !selectedVenue) return;

    try {
      setUpdatingRoleId(pendingRoleChange.userId);
      const userRef = doc(db, "users", pendingRoleChange.userId);
      const venueRef = doc(db, "venues", selectedVenue);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      const targetRole = pendingRoleChange.newRole;

      // Update user document
      await updateDoc(userRef, {
        role: targetRole,
        updatedAt: new Date(),
        venues: arrayUnion(selectedVenue),
      });

      // Update venue document (notify only for owners)
      if (targetRole === "owner") {
        await updateDoc(venueRef, {
          notify: arrayUnion(userData.email),
        });
      }

      // Update local state
      setUsers(
        users.map((user) =>
          user.id === pendingRoleChange.userId
            ? {
                ...user,
                role: targetRole,
                venues: [...(user.venues || []), selectedVenue],
              }
            : user
        )
      );

      showAlert("Rol y venue asignados exitosamente", "success");
      setIsVenueModalOpen(false);
      setSelectedVenue("");
      setPendingRoleChange(null);
      setUpdatingRoleId(null);
    } catch (error) {
      console.error("Error updating role and venue:", error);
      showAlert("Error al actualizar el rol y venue", "error");
      setUpdatingRoleId(null);
    }
  };

  const handleRemoveVenue = async (userId, venueId, userEmail) => {
    try {
      setRemovingVenue({ userId, venueId });
      const userRef = doc(db, "users", userId);
      const venueRef = doc(db, "venues", venueId);

      // Update user document - remove venue from venues array
      await updateDoc(userRef, {
        venues: arrayRemove(venueId),
      });

      // Update venue document - remove user email from notify array
      await updateDoc(venueRef, {
        notify: arrayRemove(userEmail),
      });

      // Update local state
      setUsers(
        users.map((user) =>
          user.id === userId
            ? {
                ...user,
                venues: (user.venues || []).filter((id) => id !== venueId),
              }
            : user
        )
      );

      showAlert("Venue removido exitosamente", "success");
    } catch (error) {
      console.error("Error removing venue:", error);
      showAlert("Error al remover el venue", "error");
    } finally {
      setRemovingVenue(null);
    }
  };

  if (loading) return <div className="p-4">Cargando usuarios...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <div className="container mx-auto p-4 space-y-8 max-lg:px-0">
      <div>
        <div className="sm:flex sm:items-center sm:justify-between">
          <Heading level={2}>Gestión de Usuarios</Heading>
          <div className="mt-4 sm:mt-0 flex items-center gap-4">
            <span className="text-sm text-gray-700">
              Total: {filteredUsers.length} usuarios
            </span>
            <div className="inline-flex rounded-md shadow-sm">
              <button
                onClick={() => setViewMode("list")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-l-md border ${
                  viewMode === "list"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                <ListBulletIcon className="h-4 w-4" />
                Lista
              </button>
              <button
                onClick={() => setViewMode("venue")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-r-md border-t border-r border-b ${
                  viewMode === "venue"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                <BuildingOfficeIcon className="h-4 w-4" />
                Por Venue
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 mb-4 sm:flex sm:items-center sm:justify-between gap-4">
          <div className="flex flex-1 gap-4 max-w-2xl">
            <div className="relative flex-1">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
              <input
                type="text"
                className="block w-full rounded-md border-0 py-1.5 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                placeholder="Buscar por nombre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="relative flex-1">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <EnvelopeIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
              <input
                type="text"
                className="block w-full rounded-md border-0 py-1.5 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                placeholder="Buscar por email..."
                value={emailSearchQuery}
                onChange={(e) => setEmailSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-4 sm:mt-0 sm:flex-none flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="block rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
            >
              {FILTER_ROLES.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            <select
              value={selectedVenueFilter}
              onChange={(e) => setSelectedVenueFilter(e.target.value)}
              className="block rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
            >
              <option value="all">Todos los venues</option>
              {venues.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {viewMode === "venue" && (
          <div className="mt-4 grid gap-6">
            {venueGroups.map(({ venue, users: venueUsers }) => (
              <div
                key={venue.id}
                className="ring-1 ring-gray-200 rounded-lg overflow-hidden"
              >
                <div className="bg-gray-50 px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BuildingOfficeIcon className="h-5 w-5 text-gray-500" />
                    <span className="text-sm font-semibold text-gray-900">
                      {venue.name}
                    </span>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {venueUsers.length} {venueUsers.length === 1 ? "usuario" : "usuarios"}
                  </span>
                </div>
                {venueUsers.length > 0 ? (
                  <ul className="divide-y divide-gray-100">
                    {venueUsers.map((user) => (
                      <li
                        key={user.id}
                        className="px-6 py-3 flex items-center justify-between hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-xs font-medium text-gray-500">
                              {user.name ? user.name[0] : "?"}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.name
                                ? `${user.name} ${user.lastName || ""}`
                                : "N/A"}
                            </div>
                            <div className="text-xs text-gray-500">
                              {user.email || "N/A"}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              user.role === "admin"
                                ? "bg-purple-100 text-purple-800"
                                : user.role === "owner"
                                ? "bg-orange-100 text-orange-800"
                                : user.role === "planner"
                                ? "bg-blue-100 text-blue-800"
                                : user.role === "viewer"
                                ? "bg-cyan-100 text-cyan-800"
                                : user.role === "inversionista"
                                ? "bg-indigo-100 text-indigo-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {user.role || "user"}
                          </span>
                          <span className="text-xs text-gray-400">
                            {user.phone || ""}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="px-6 py-4 text-sm text-gray-400">
                    Sin usuarios asignados
                  </div>
                )}
              </div>
            ))}

            {usersWithoutVenue.length > 0 && (
              <div className="ring-1 ring-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-500">
                      Sin Venue
                    </span>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    {usersWithoutVenue.length} {usersWithoutVenue.length === 1 ? "usuario" : "usuarios"}
                  </span>
                </div>
                <ul className="divide-y divide-gray-100">
                  {usersWithoutVenue.map((user) => (
                    <li
                      key={user.id}
                      className="px-6 py-3 flex items-center justify-between hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-500">
                            {user.name ? user.name[0] : "?"}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.name
                              ? `${user.name} ${user.lastName || ""}`
                              : "N/A"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {user.email || "N/A"}
                          </div>
                        </div>
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          user.role === "admin"
                            ? "bg-purple-100 text-purple-800"
                            : user.role === "owner"
                            ? "bg-orange-100 text-orange-800"
                            : user.role === "planner"
                            ? "bg-blue-100 text-blue-800"
                            : user.role === "viewer"
                            ? "bg-cyan-100 text-cyan-800"
                            : user.role === "inversionista"
                            ? "bg-indigo-100 text-indigo-800"
                            : user.role === "client"
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {user.role || "user"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {viewMode === "list" && (<div className="mt-4">
          <div className="overflow-x-auto ring-1 ring-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre Completo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Teléfono
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Venues
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort(SORT_FIELDS.LAST_LOGIN)}
                  >
                    <div className="flex items-center gap-1">
                      Último Acceso
                      {getSortIcon(SORT_FIELDS.LAST_LOGIN)}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort(SORT_FIELDS.CREATED_AT)}
                  >
                    <div className="flex items-center gap-1">
                      Fecha de Registro
                      {getSortIcon(SORT_FIELDS.CREATED_AT)}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-500">
                              {user.name ? user.name[0] : "?"}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name
                              ? `${user.name} ${user.lastName || ""}`
                              : "N/A"}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {user.uid || user.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.email || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.phone || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="relative role-dropdown">
                        <button
                          onClick={() =>
                            setUpdatingRoleId(
                              updatingRoleId === user.id ? null : user.id
                            )
                          }
                          disabled={updatingRoleId === user.id}
                          className={`group inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                            user.role === "admin"
                              ? "bg-purple-100 text-purple-800 hover:bg-purple-200"
                              : user.role === "client"
                              ? "bg-green-100 text-green-800 hover:bg-green-200"
                              : user.role === "planner"
                              ? "bg-blue-100 text-blue-800 hover:bg-blue-200"
                              : user.role === "owner"
                              ? "bg-orange-100 text-orange-800 hover:bg-orange-200"
                              : user.role === "viewer"
                              ? "bg-cyan-100 text-cyan-800 hover:bg-cyan-200"
                              : user.role === "inversionista"
                              ? "bg-indigo-100 text-indigo-800 hover:bg-indigo-200"
                              : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                          }`}
                        >
                          {user.role || "user"}
                          {updatingRoleId === user.id ? (
                            <svg
                              className="animate-spin h-3 w-3"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          ) : (
                            <svg
                              className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </button>
                        {updatingRoleId === user.id && (
                          <div className="absolute z-10 mt-1 w-48 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {ROLES.map((role) => (
                              <button
                                key={role.id}
                                onClick={() =>
                                  handleRoleChange(user.id, role.id)
                                }
                                className={`block w-full px-4 py-2 text-left text-sm ${
                                  user.role === role.id
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                {role.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {ROLES_CON_VENUES.includes(user.role) && (
                          <>
                            <div className="flex flex-wrap gap-1">
                              {user.venues?.map((venueId) => {
                                const venue = venues.find(
                                  (v) => v.id === venueId
                                );
                                return venue ? (
                                  <span
                                    key={venueId}
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${user.role === "viewer" ? "bg-gray-100 text-gray-700" : user.role === "inversionista" ? "bg-indigo-50 text-indigo-800" : "bg-blue-100 text-blue-800"}`}
                                  >
                                    {venue.name}
                                    <button
                                      onClick={() =>
                                        handleRemoveVenue(
                                          user.id,
                                          venueId,
                                          user.email
                                        )
                                      }
                                      disabled={
                                        removingVenue?.userId === user.id &&
                                        removingVenue?.venueId === venueId
                                      }
                                      className={`inline-flex items-center p-0.5 rounded-full focus:outline-none ${user.role === "viewer" ? "text-gray-500 hover:bg-gray-200" : user.role === "inversionista" ? "text-indigo-600 hover:bg-indigo-100" : "text-blue-600 hover:bg-blue-200"}`}
                                    >
                                      {removingVenue?.userId === user.id &&
                                      removingVenue?.venueId === venueId ? (
                                        <svg
                                          className="animate-spin h-3 w-3"
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          ></circle>
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                          ></path>
                                        </svg>
                                      ) : (
                                        <XMarkIcon className="h-3 w-3" />
                                      )}
                                    </button>
                                  </span>
                                ) : null;
                              })}
                            </div>
                            <button
                              onClick={() => {
                                setPendingRoleChange({
                                  userId: user.id,
                                  newRole: user.role,
                                });
                                setIsVenueModalOpen(true);
                              }}
                              className="inline-flex items-center p-1 border border-transparent rounded-full text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <PlusCircleIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(user.lastLogin)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(user.createdAt)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>)}
      </div>

      {/* Venue Assignment Modal */}
      <Dialog
        open={isVenueModalOpen}
        onClose={() => {
          if (!updatingRoleId) {
            setIsVenueModalOpen(false);
            setSelectedVenue("");
            setPendingRoleChange(null);
          }
        }}
        title="Asignar Venue"
      >
        <DialogBody>
          <div className="mt-4">
            <label
              htmlFor="venue"
              className="block text-sm font-medium text-gray-700"
            >
              Seleccionar Venue
            </label>
            <select
              id="venue"
              value={selectedVenue}
              onChange={(e) => setSelectedVenue(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              disabled={updatingRoleId}
            >
              <option value="">Selecciona un venue...</option>
              {venues.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}
                </option>
              ))}
            </select>
          </div>
        </DialogBody>

        <DialogActions>
          <Button
            plain
            onClick={() => {
              if (!updatingRoleId) {
                setIsVenueModalOpen(false);
                setSelectedVenue("");
                setPendingRoleChange(null);
              }
            }}
            disabled={updatingRoleId}
          >
            Cancelar
          </Button>
          <Button
            color="blue"
            onClick={handleVenueAssignment}
            disabled={!selectedVenue || updatingRoleId}
          >
            {updatingRoleId ? "Procesando..." : "Asignar"}
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default Users;
